/**
 * 交通費の月次再計算(P-38。2026-09-16 実装)。
 *
 * 1日の交通費は取込のたびに「その日の実稼働」で決まる(0/30/50%)。
 * **2026-09-22 から、月100時間以上の70%は 管理者が確認・確定した人だけ**(発注者指示。D-128)。
 * 確定していない人は100時間を超えても 0/30/50% のまま。「要確認」としてダッシュボードと日報/サマリに出す。
 * 確定した人は、その月に稼働した全日を70%にする(あとから取り込み直しても70%のまま)。
 *
 * 同じ人が品川の登録を2つ(給湯器・ホワイト)持つ日は**合算して判定する**(2026-09-16 発注者判断)。
 * マスタでは「小原　南風」と「小原　南風W」のように**別のオペレーターとして登録されている場合がある**ため、
 * 氏名(空白と末尾のW/Ｗを除く)で同じ人とみなす。
 */
import { COMMUTE_MONTHLY_THRESHOLD_HOURS, commuteAmount } from '@core/lib/commute';
import type { DailySummary, StaffDaily } from '@core/lib/types';

/** 同じ人とみなすキー(空白と末尾の W/Ｗ を落とした氏名) */
export function personKey(s: Pick<StaffDaily, 'name' | 'op_id'>): string {
  const name = (s.name ?? '').replace(/[\s　]/g, '').replace(/[WＷ]+$/, '');
  return name || s.op_id;
}

const isShinagawa = (s: StaffDaily) => s.work_style === 'shinagawa';

/** その日の「人ごとの品川の実稼働時間」 */
export function shinagawaHoursByPerson(staff: StaffDaily[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of staff) {
    if (!isShinagawa(s)) continue;
    out.set(personKey(s), (out.get(personKey(s)) ?? 0) + (s.worked_hours ?? 0));
  }
  return out;
}

type DaySummary = Pick<DailySummary, 'staff'> & { id: string };

/**
 * 1か月分の daily_summary の交通費を計算し直す。**中身は変えず、変わった日だけ返す**(呼び出し側が保存する)。
 * `commute_base`(マスタの交通費)が入っていない古い行は触らない。
 * @param confirmed 70%を確定した人(personKey)。**ここに入っていない人は、月100時間を超えても 0/30/50% のまま**(D-128)
 */
export function settleCommuteForMonth<T extends DaySummary>(days: T[], confirmed: Set<string> = new Set()): { id: string; staff: StaffDaily[] }[] {
  const monthHours = new Map<string, number>();
  for (const d of days) for (const [key, h] of shinagawaHoursByPerson(d.staff ?? [])) monthHours.set(key, (monthHours.get(key) ?? 0) + h);

  const changed: { id: string; staff: StaffDaily[] }[] = [];
  for (const d of days) {
    const dayHours = shinagawaHoursByPerson(d.staff ?? []);
    let dirty = false;
    const staff = (d.staff ?? []).map((s) => {
      const base = s.commute_base ?? 0;
      if (!isShinagawa(s) || base <= 0) return s;
      const key = personKey(s);
      // 確定した人は その月の全日を70%(100時間を下回っても管理者の決定どおり)。確定していない人は その日の実稼働で 0/30/50%
      const month = confirmed.has(key) ? Math.max(monthHours.get(key) ?? 0, COMMUTE_MONTHLY_THRESHOLD_HOURS) : 0;
      const next = commuteAmount(base, dayHours.get(key) ?? 0, month);
      if (next === s.commute) return s;
      dirty = true;
      return { ...s, commute: next, pay: s.pay - s.commute + next };
    });
    if (dirty) changed.push({ id: d.id, staff });
  }
  return changed;
}

/** 交通費の「要確認」1人ぶん(月100時間以上。D-128) */
export type CommuteCandidate = {
  /** 同じ人とみなすキー(personKey) */
  key: string;
  name: string;
  /** その月の出勤(品川)の実稼働(h) */
  hours: number;
  /** 交通費が付く日数(実稼働がある日) */
  days: number;
  /** いまの交通費の合計(保存されている値) */
  current: number;
  /** 全日70%にしたときの合計 */
  at70: number;
  /** 確定済みか */
  confirmed: boolean;
};

/**
 * その月の交通費の「要確認」一覧。**出勤(品川)の実稼働が月100時間以上で、マスタ交通費がある人**と、確定済みの人。
 * マスタ交通費が0円の人は、70%にしても金額が変わらないので出さない
 */
export function commuteCandidates<T extends DaySummary>(days: T[], confirmed: Set<string> = new Set()): CommuteCandidate[] {
  const byPerson = new Map<string, { name: string; hours: number; days: number; current: number; at70: number; hasBase: boolean }>();
  /** 交通費が付いた日(同じ人が1日に2行あっても1日と数える) */
  const counted = new Set<string>();
  for (const d of days) {
    const dayHours = shinagawaHoursByPerson(d.staff ?? []);
    for (const s of d.staff ?? []) {
      if (!isShinagawa(s)) continue;
      const key = personKey(s);
      const p = byPerson.get(key) ?? { name: (s.name ?? '').replace(/[WＷ]+$/, ''), hours: 0, days: 0, current: 0, at70: 0, hasBase: false };
      p.hours += s.worked_hours ?? 0;
      const base = s.commute_base ?? 0;
      if (base > 0) {
        p.hasBase = true;
        p.current += s.commute ?? 0;
        if ((dayHours.get(key) ?? 0) > 0) {
          if (!counted.has(`${key}|${d.id}`)) {
            counted.add(`${key}|${d.id}`);
            p.days += 1;
          }
          p.at70 += commuteAmount(base, dayHours.get(key) ?? 0, COMMUTE_MONTHLY_THRESHOLD_HOURS);
        }
      }
      byPerson.set(key, p);
    }
  }
  return [...byPerson.entries()]
    .filter(([key, p]) => p.hasBase && (p.hours >= COMMUTE_MONTHLY_THRESHOLD_HOURS || confirmed.has(key)))
    .map(([key, p]) => ({ key, name: p.name, hours: Math.round(p.hours * 100) / 100, days: p.days, current: p.current, at70: p.at70, confirmed: confirmed.has(key) }))
    .sort((a, b) => Number(a.confirmed) - Number(b.confirmed) || b.hours - a.hours);
}
